<template>
    <div v-if="visible"
         class="editable-segment-field">
        <hh-form-group class="editable-segment-field__form-group"
                       :def="def"
                       :label-text="value.title">
            <segment-data-editor v-if="isEditing"
                                 class="editable-segment-field__editor"
                                 v-model="editor"
                                 :options="options"
                                 :type="value.type" />
            <span v-else class="editable-segment-field__display">
                {{ displayModeText }}
            </span>
            <hh-info-attachment v-if="value.description"
                                class="editable-segment-field__info"
                                :title="value.title"
                                :text="value.description"
                                :image-url="value.imageUrl" />
        </hh-form-group>
    </div>
</template>

<script>

import _ from 'lodash'
import Vuex from 'vuex'

import Casing from '../../helpers/casing'
import Config from '../../app.config'

import EditablePropertyDataItem from '../../models/editable-property-data-item'

import PropertyManager from '../../property-manager'

import SegmentDataEditor from './segment-data-editor'

const loggingCategory = 'Rules - Editable Field'

export default {

    props: {
        value: {
            type: Object,
            default: null,
        },
        dataManager: {
            type: Object,
            required: true,
        },
        dataManagerUpdateKey: {
            type: Number,
            required: true,
        },
        editing: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            lastSetValue: null,
        }
    },

    computed: {

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Array<EditablePropertyDataItem>}
             */
            edits: stateStore => stateStore.myHome.edits
                ? stateStore.myHome.edits.map(edit => EditablePropertyDataItem.ensure(edit)) : [],
        }),

        allOptions() {
            return this.value && this.value.selectionItems && this.value.selectionItems.length
                ? this.value.selectionItems
                : this.value && this.value.value != null ? [ this.value.value ] : []
        },

        def() {
            return this.value && this.value.dottedString ? `${ Casing.kebabCase(this.value.dottedString) }-group` : 'null-value-group'
        },

        displayModeText() {
            return this.isBoolean
                ? this.value.value == null ? 'Unknown' : this.value.value ? 'Yes' : 'No'
                : this.value.value !== undefined && this.value.value !== null ? String(this.value.value) : ''
        },

        editor: {
            get() {
                return this.edits.find(edit => edit.dottedString == this.value.dottedString)
            },
            set(value) {
                this._updateCascade(value)
            },
        },

        isBoolean() {
            return this.value.type === 'Boolean'
        },

        isEditing() {
            return this.editing && !this.isReadOnly
        },

        isReadOnly() {
            return this.value.type === 'ReadOnly'
        },

        options() {
            return this.allOptions
                .filter(item => this.dataManager.shouldShowItem(item))
                .map(item => {
                    return {
                        value: item.id,
                        text: item.value,
                    }
                })
        },

        visible() {
            return this.value && this.dataManager.shouldShowItem(this.value)
        },
    },

    watch: {

        options(newValue) {
            if (newValue.filter(item => item.value == this.value.id || item.value == this.lastSetValue).length == 0) {
                if (this.editor) {
                    this.editor.editedValue = newValue && newValue.length ? newValue[0].value : null
                    this._setLastSetTo(newValue[0])
                    this._updateCascade(this.editor)
                }
            }
        },

        value(newValue) {
            this._setLastSetTo(newValue)
        },

        visible(newValue) {
            if (newValue && this.editor) {
                const option = this.options && this.options.length ? this.options[0] : null
                this.editor.editedValue = option ? option.value : null
                this._setLastSetTo(option)
                this._updateCascade(this.editor)
            }
        },
    },

    methods: {

        _setLastSetTo(item) {
            this.lastSetValue = item != null
                ? item.type == 'Selection' ? item.id : item.value
                : null
        },

        _updateCascade(value) {
            if (!value) return

            const updatedDataItem = _.cloneDeep(this.value)
            if (this.value.type === 'Selection') {
                if (this.allOptions.length) {
                    const item = this.allOptions.find(si => si.id === value.editedValue)
                    if (item) {
                        updatedDataItem.id = item.id
                        updatedDataItem.value = item.value
                    } else {
                        updatedDataItem.id = null
                        updatedDataItem.value = null
                    }
                }
            } else {
                updatedDataItem.value = value.editedValue
            }

            this.dataManager.updateComparisonValuesState(updatedDataItem)

            if (Config.verboseRuleLogging)
                this.$logger.logObject({ def: this.def, oldValue: this.value, newValue: updatedDataItem }, `${ this.def } value changed`, loggingCategory)

            value.payload = value.changed || this.visible ? updatedDataItem : null
            this._setLastSetTo(updatedDataItem)
            PropertyManager.Edits.update(value)
            this.$emit('input', updatedDataItem)
        },
    },

    components: {
        SegmentDataEditor,
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\my-home\_editable-segment-field.scss

.editable-segment-field {
    position: relative;
    @include user-select-none;

    @include element('display') {
        line-height: 0.6rem;
        padding: 0 0.6rem;
        display: inline-block;
        width: 100%;

        @include bp-min-small {
            line-height: inherit;
        }
    }

    @include element('info') {
        position: absolute;
        top: -0.4rem;
        right: 0.1rem;

        @include bp-min-small {
            top: 0.2rem;
            right: -0.4rem;
        }

        @include bp-min-wide {
            right: 0;
        }
    }

    .form {
        @include element('group') {

            @include bp-min-small {
                display: flex;
                flex-direction: row;
                flex-wrap: nowrap;
                justify-content: flex-start;
                align-items: center;
                min-height: 2.5rem;
            }
        }

        @include element('label') {
            font-weight: bold;
            font-size: 0.9rem;
            line-height: 1rem;
            max-width: 90%;

            &:after {
                content: ': '
            }

            @include bp-min-small {
                width: 30%;
            }

            @include bp-min-mid {
                width: 44%;
            }

            @include bp-min-wider {
                width: 39%;
            }
        }

        @include element('field-wrapper') {
            min-height: 2.6rem;
            font-size: 0.9rem;

            @include bp-min-small {
                min-height: auto;
                display: inline-block;
                width: 68%;
            }

            @include bp-min-mid {
                width: 55%;
            }

            @include bp-min-wider {
                width: 60%;
            }
        }

        @include element('field') {
            font-size: 0.9rem;
        }
    }
}

</style>

